import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GitMerge, X, Check } from "lucide-react";

export default function AdminMergeProposals() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/merge-proposals")
            .then(({ data }) => setItems(data.items))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const accept = async (id) => {
        setBusy(id);
        try {
            await api.post(`/admin/merge-proposals/${id}/accept`, { merge_formats: true });
            toast.success("Proposta accettata (merge eseguito)");
            load();
        } catch {
            toast.error("Errore");
        } finally { setBusy(null); }
    };
    const reject = async (id) => {
        setBusy(id);
        try {
            const { data } = await api.post(`/admin/merge-proposals/${id}/reject`);
            toast.success(`Rifiutata: nuovo vino creato (${data.created_wine_id})`);
            load();
        } catch {
            toast.error("Errore");
        } finally { setBusy(null); }
    };

    return (
        <div data-testid="admin-merge-proposals-page">
            <h1 className="text-3xl mb-2">Proposte di Merge</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6 max-w-2xl">
                Quando un import identifica un possibile duplicato di un vino già in catalogo, non
                viene mai unito automaticamente: qui puoi accettare (merge) o rifiutare (crea vino
                separato).
            </p>

            {loading && <div className="text-[hsl(var(--muted-foreground))]">Caricamento…</div>}
            {!loading && items.length === 0 && (
                <div className="italic text-[hsl(var(--muted-foreground))]">Nessuna proposta in attesa.</div>
            )}

            <div className="space-y-3">
                {items.map((p) => (
                    <div key={p.id} className="bg-white border border-[hsl(var(--border))] rounded-sm p-5" data-testid={`merge-proposal-${p.id}`}>
                        <div className="flex items-start gap-3 mb-3">
                            <GitMerge className="w-4 h-4 mt-1 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                            <div className="flex-1">
                                <div className="co-caption">Duplicato rilevato da import {p.source?.toUpperCase()}</div>
                                <div className="text-lg mt-1">Fondere queste due schede?</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                            <div className="p-3 bg-[hsl(var(--secondary))] rounded-sm">
                                <div className="co-caption mb-1">In catalogo</div>
                                <div className="text-base">{p.target_wine?.name || <span className="italic text-[hsl(var(--muted-foreground))]">senza nome</span>}</div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                    {p.target_wine?.producer || "—"} · {p.target_wine?.vintage || "—"}
                                    {p.target_wine?.formats?.length ? ` · ${p.target_wine.formats.length} formato/i` : ""}
                                </div>
                            </div>
                            <div className="p-3 bg-white border border-[hsl(var(--accent))]/40 rounded-sm">
                                <div className="co-caption mb-1">Nuovo (proposta)</div>
                                <div className="text-base">{p.candidate?.name}</div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))]">{p.candidate?.producer} · {p.candidate?.vintage || "—"} · € {p.candidate?.base_price_public}</div>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => accept(p.id)}
                                disabled={busy === p.id}
                                className="co-btn-primary"
                                data-testid={`merge-accept-${p.id}`}
                            >
                                <Check className="w-4 h-4" /> Fondi (merge)
                            </button>
                            <button
                                onClick={() => reject(p.id)}
                                disabled={busy === p.id}
                                className="co-btn-ghost"
                                data-testid={`merge-reject-${p.id}`}
                            >
                                <X className="w-4 h-4" /> Rifiuta (crea separato)
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
